import { Seo } from "../components/Seo";
import { useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Store,
  Search,
  ChevronRight,
  CheckCircle2,
  Bell,
  LineChart,
  Megaphone,
  ShoppingCart,
  FileCheck,
  Shield,
  Bot,
  Zap,
  Users
} from "lucide-react";
import { toast } from "sonner";

const agentCategories = [
  { name: "DeFi & Trading", icon: LineChart, count: 12, color: "swarm-violet" },
  { name: "Marketing & SEM", icon: Megaphone, count: 8, color: "swarm-cyan" },
  { name: "E-commerce", icon: ShoppingCart, count: 6, color: "swarm-green" },
  { name: "Procurement", icon: FileCheck, count: 4, color: "swarm-violet" },
  { name: "Compliance", icon: Shield, count: 5, color: "swarm-cyan" },
  { name: "Customer Support", icon: Bot, count: 7, color: "swarm-green" },
];

const featuredAgents = [
  {
    name: "DeFi Yield Optimizer",
    provider: "AlphaSwarm Labs",
    category: "DeFi & Trading",
    description: "Automated yield farming across major DeFi protocols with risk-adjusted portfolio management.",
    permissions: ["Execute trades", "Access wallet balances", "Monitor positions"],
    rating: 4.8,
    installs: "2.4k"
  },
  {
    name: "SEM Campaign Manager",
    provider: "AdFlow AI",
    category: "Marketing & SEM",
    description: "Intelligent Google Ads and Meta Ads management with automated bid optimization.",
    permissions: ["Manage ad campaigns", "Adjust budgets", "Create ad copy"],
    rating: 4.6,
    installs: "1.8k"
  },
  {
    name: "Inventory Forecaster",
    provider: "SupplyMind",
    category: "E-commerce",
    description: "Predictive inventory management with automated reorder points and demand forecasting.",
    permissions: ["Read inventory data", "Create purchase orders", "Send notifications"],
    rating: 4.7,
    installs: "950"
  }
];

export const MarketplacePage = () => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleNotify = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsSubmitting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/newsletter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email })
      });
      
      if (response.ok) {
        setIsSubscribed(true);
        toast.success("You'll be notified when the marketplace launches!");
      } else {
        const data = await response.json();
        if (data.detail === "Email already subscribed") {
          toast.info("You're already on the list!");
        } else {
          throw new Error();
        }
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-24" data-testid="marketplace-page">
      <Seo title="Marketplace | SwarmCitadel" description="The SwarmCitadel marketplace for governed AI agent integrations." path="/marketplace" />
      {/* Hero */}
      <section className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-mesh" />
        <div className="absolute top-1/3 left-1/4 w-[600px] h-[600px] bg-swarm-green/10 rounded-full blur-[150px]" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            {/* Early Access Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-swarm-green/10 border border-swarm-green/30 mb-8">
              <Zap className="w-4 h-4 text-swarm-green" />
              <span className="text-swarm-green text-sm font-medium">Early Access</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-6">
              Agent Marketplace
            </h1>
            <p className="text-lg text-slate-400 leading-relaxed mb-10">
              The place where AI agent providers meet companies that need them.
              Browse, install, govern, and go.
            </p>

            {/* Notify Form */}
            {!isSubscribed ? (
              <form onSubmit={handleNotify} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-swarm-darker border-swarm-border text-white h-12 flex-1"
                  data-testid="marketplace-email"
                />
                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="bg-swarm-green hover:bg-swarm-green/90 h-12 px-6"
                  data-testid="marketplace-notify"
                >
                  <Bell className="w-4 h-4 mr-2" />
                  Notify Me
                </Button>
              </form>
            ) : (
              <div className="flex items-center justify-center gap-2 text-swarm-green">
                <CheckCircle2 className="w-5 h-5" />
                <span>You're on the list!</span>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Two Columns: For Providers / For Operators */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* For Agent Providers */}
            <div className="glass-card p-8 border-swarm-violet/30">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-xl bg-swarm-violet/10">
                  <Bot className="w-6 h-6 text-swarm-violet" />
                </div>
                <h2 className="text-2xl font-bold text-white">For Agent Providers</h2>
              </div>
              <p className="text-slate-400 mb-6">
                Publish your agents once. Reach every operator on the platform. 
                SDK, documentation, and revenue share (70-85% to you).
              </p>
              <ul className="space-y-3">
                {[
                  "SDK and developer documentation",
                  "Built-in governance compliance",
                  "Usage analytics and insights",
                  "Automated revenue distribution"
                ].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-slate-300 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-swarm-violet" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* For Operators */}
            <div className="glass-card p-8 border-swarm-green/30">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-xl bg-swarm-green/10">
                  <Users className="w-6 h-6 text-swarm-green" />
                </div>
                <h2 className="text-2xl font-bold text-white">For Operators</h2>
              </div>
              <p className="text-slate-400 mb-6">
                Browse and install pre-built AI agents for DeFi trading, marketing, e-commerce, 
                procurement, compliance, and more. Every agent runs under your governance rules.
              </p>
              <ul className="space-y-3">
                {[
                  "No integration consultants",
                  "No onboarding calls",
                  "Your policies, their capabilities",
                  "Full audit trail included"
                ].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-slate-300 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-swarm-green" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 md:py-20 bg-swarm-darker/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">How It Works</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {[
              { step: "1", title: "Browse Agents", desc: "Explore agents by category" },
              { step: "2", title: "Review Permissions", desc: "See what each agent can do" },
              { step: "3", title: "Set Governance", desc: "Define rules and spending limits" },
              { step: "4", title: "Activate & Monitor", desc: "Launch from your dashboard" }
            ].map((item, idx) => (
              <div key={item.step} className="text-center">
                <div className="w-12 h-12 rounded-full bg-swarm-cyan/10 border border-swarm-cyan/30 flex items-center justify-center mx-auto mb-4 text-swarm-cyan font-bold">
                  {item.step}
                </div>
                <h3 className="text-white font-semibold mb-2">{item.title}</h3>
                <p className="text-slate-500 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Preview */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Agent Categories</h2>
            <p className="text-slate-400">Pre-built agents across industries, ready to deploy.</p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {agentCategories.map((category) => {
              const Icon = category.icon;
              const colorClasses = {
                'swarm-violet': { bg: 'bg-swarm-violet/10', text: 'text-swarm-violet' },
                'swarm-cyan': { bg: 'bg-swarm-cyan/10', text: 'text-swarm-cyan' },
                'swarm-green': { bg: 'bg-swarm-green/10', text: 'text-swarm-green' }
              };
              const colors = colorClasses[category.color];
              
              return (
                <div key={category.name} className="glass-card p-4 text-center hover:border-slate-600 transition-all cursor-pointer">
                  <div className={`w-12 h-12 rounded-xl ${colors.bg} flex items-center justify-center mx-auto mb-3`}>
                    <Icon className={`w-6 h-6 ${colors.text}`} />
                  </div>
                  <h3 className="text-white text-sm font-medium mb-1">{category.name}</h3>
                  <p className="text-slate-500 text-xs">{category.count} agents</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Agents Preview */}
      <section className="py-16 md:py-20 bg-swarm-darker/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Featured Agents</h2>
            <p className="text-slate-400">Preview of what's coming to the marketplace.</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {featuredAgents.map((agent) => (
              <div key={agent.name} className="glass-card p-6 opacity-75 hover:opacity-100 transition-all">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-white font-semibold mb-1">{agent.name}</h3>
                    <p className="text-slate-500 text-sm">by {agent.provider}</p>
                  </div>
                  <span className="px-2 py-1 bg-swarm-violet/10 text-swarm-violet text-xs rounded">
                    {agent.category}
                  </span>
                </div>
                <p className="text-slate-400 text-sm mb-4">{agent.description}</p>
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-1 text-swarm-green">
                    <span>★</span>
                    <span>{agent.rating}</span>
                  </div>
                  <span className="text-slate-500">{agent.installs} installs</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Store className="w-16 h-16 text-swarm-green mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Be First to Access the Marketplace
          </h2>
          <p className="text-slate-400 text-lg mb-8">
            Join the waitlist and get early access when we launch.
          </p>
          {!isSubscribed ? (
            <form onSubmit={handleNotify} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-swarm-darker border-swarm-border text-white h-12 flex-1"
              />
              <Button 
                type="submit" 
                disabled={isSubmitting}
                className="bg-swarm-green hover:bg-swarm-green/90 h-12 px-6"
              >
                Get Notified
              </Button>
            </form>
          ) : (
            <div className="flex items-center justify-center gap-2 text-swarm-green">
              <CheckCircle2 className="w-5 h-5" />
              <span>You're on the list!</span>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default MarketplacePage;
